/**
 * CalendarWidget — Mini month calendar + upcoming events list.
 *
 * Reads from calendar_event via the middleware's /calendar/events route.
 * The previous implementation pointed at /calendar_event (data-layer path,
 * which the gateway blocks) and sorted by `event_date` (the entity field is
 * `start_date`) — both silent 404s that produced an empty state forever.
 *
 * If the fetch returns zero events on first mount, we trigger POST
 * /calendar/sync once to project events from their source entities (CT,
 * insurance, maintenance, …). Without this, a tenant sees "Aucun événement"
 * until someone manually clicks Synchroniser on the full calendar page.
 */
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { calendarAPI } from '../../../../api/calendar.js';
import { Card, CardHeader, CardTitle } from '../../shared/index.js';
import { useFormat } from '../../../../i18n/format.js';

// Libellés : dashboard.calendar.types.<event_type>
const EVENT_TYPE_STYLES = {
  ct_expiry:    { dot: 'bg-amber-500',   badge: 'text-amber-700 bg-amber-100' },
  insurance:    { dot: 'bg-blue-500',    badge: 'text-blue-700 bg-blue-100' },
  maintenance:  { dot: 'bg-emerald-500', badge: 'text-emerald-700 bg-emerald-100' },
  inspection:   { dot: 'bg-purple-500',  badge: 'text-purple-700 bg-purple-100' },
  license:      { dot: 'bg-indigo-500',  badge: 'text-indigo-700 bg-indigo-100' },
  fine:         { dot: 'bg-red-500',     badge: 'text-red-700 bg-red-100' },
  claim:        { dot: 'bg-orange-500',  badge: 'text-orange-700 bg-orange-100' },
  scheduled:    { dot: 'bg-gray-400',    badge: 'text-gray-700 bg-gray-100' },
};

const EVENT_NAVIGATION = {
  ct_expiry: 'vehicles',
  insurance: 'insurance',
  maintenance: 'maintenance',
  inspection: 'inspections',
  license: 'drivers',
  fine: 'fines',
  claim: 'claims',
  scheduled: 'calendar',
};

// En-têtes de colonnes lundi→dimanche, initiales dans la locale active
// (fr : L M M J V S D · en : M T W T F S S). 2024-01-01 est un lundi.
function weekdayInitials(locale) {
  const f = new Intl.DateTimeFormat(locale, { weekday: 'narrow' });
  return Array.from({ length: 7 }, (_, i) => f.format(new Date(2024, 0, 1 + i)));
}

function capitalize(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function isSameDay(a, b) { return startOfDay(a).getTime() === startOfDay(b).getTime(); }

// Local-time YYYY-MM-DD. toISOString() uses UTC, which shifts the day for any
// timezone east of UTC (including France) — that silently mis-buckets events
// that fall between 22:00 and 00:00 local.
function isoDayLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// « Aujourd'hui / Demain / Hier » sinon « mar. 25 août » dans la locale active.
function formatDateShort(t, fmt, date) {
  const d = startOfDay(date);
  const today = startOfDay(new Date());
  const diffDays = Math.round((d - today) / 86400000);
  if (diffDays === 0) return t('dashboard.calendar.today');
  if (diffDays === 1) return t('dashboard.calendar.tomorrow');
  if (diffDays === -1) return t('dashboard.calendar.yesterday');
  return fmt.date(d, { weekday: 'short', day: 'numeric', month: 'short' });
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = startOfDay(new Date(dateStr));
  const now = startOfDay(new Date());
  return Math.round((d - now) / 86400000);
}

export function CalendarWidget({ onNavigate }) {
  const { t, fmt, locale } = useFormat();
  const queryClient = useQueryClient();
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [selectedDay, setSelectedDay] = useState(null);
  const syncTriggered = useRef(false);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['calendarWidgetEvents'],
    queryFn: async () => {
      const res = await calendarAPI.getEvents({ _limit: '500' });
      if (!res.ok) return [];
      return res.data || [];
    },
    staleTime: 120000,
  });

  // If the first successful load came back empty, project the source entities
  // into calendar_event then refetch. Only fires once per widget mount.
  useEffect(() => {
    if (isLoading) return;
    if (syncTriggered.current) return;
    if (events.length > 0) return;
    syncTriggered.current = true;
    (async () => {
      try {
        await calendarAPI.sync();
        queryClient.invalidateQueries({ queryKey: ['calendarWidgetEvents'] });
      } catch (err) {
        console.warn('[CalendarWidget] sync failed:', err?.message || err);
      }
    })();
  }, [isLoading, events.length, queryClient]);

  // Group events by YYYY-MM-DD of start_date for O(1) day lookups.
  const eventsByDay = useMemo(() => {
    const map = new Map();
    for (const e of events) {
      if (!e.start_date) continue;
      const key = String(e.start_date).slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return map;
  }, [events]);

  // Upcoming events from today forward, sorted by date (for the list under
  // the calendar when no specific day is selected).
  const upcoming = useMemo(() => {
    const today = isoDayLocal(new Date());
    return events
      .filter(e => e.start_date && String(e.start_date).slice(0, 10) >= today)
      .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)))
      .slice(0, 5);
  }, [events]);

  // When a day is selected, the list shows only that day; otherwise upcoming.
  const listEvents = useMemo(() => {
    if (!selectedDay) return upcoming;
    const key = isoDayLocal(selectedDay);
    return eventsByDay.get(key) || [];
  }, [selectedDay, upcoming, eventsByDay]);

  // Build the month grid. Monday-first (French convention) — getDay() returns
  // 0=Sunday, so we remap: (day + 6) % 7 gives Mon=0..Sun=6.
  const calendarCells = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startCol = (firstOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startCol; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(year, month, d));
    }
    return cells;
  }, [viewMonth]);

  const today = startOfDay(new Date());
  const prevMonth = () => {
    setSelectedDay(null);
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setSelectedDay(null);
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
  };
  const goToToday = () => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    setViewMonth(d);
    setSelectedDay(startOfDay(new Date()));
  };

  const handleEventClick = (event) => {
    const target = EVENT_NAVIGATION[event.event_type] || 'calendar';
    onNavigate?.(target, { selectedId: event.entity_id || event.source_id });
  };

  const monthLabel = capitalize(fmt.date(viewMonth, 'monthYear'));
  const dayHeaders = useMemo(() => weekdayInitials(locale), [locale]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between w-full">
          <CardTitle icon={Calendar}>{t('dashboard.calendar.title')}</CardTitle>
          <button
            onClick={() => onNavigate?.('calendar')}
            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
          >
            {t('dashboard.calendar.viewAll')} <ChevronRight size={12} />
          </button>
        </div>
      </CardHeader>
      {/* Two-column layout: compact mini-cal on the left, event list on the
          right. On narrow screens we fall back to stacked (flex-col). */}
      <div className="p-4 pt-0 flex flex-col md:flex-row md:gap-6">
        {/* ── Left: mini month calendar ─────────────────────────────── */}
        <div className="md:w-[42%] md:shrink-0">
          {/* Month nav */}
          <div className="flex items-center justify-between mb-1">
            <button
              onClick={prevMonth}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
              aria-label={t('dashboard.calendar.prevMonth')}
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={goToToday}
              className="text-xs font-semibold text-gray-900 hover:text-blue-600"
            >
              {monthLabel}
            </button>
            <button
              onClick={nextMonth}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
              aria-label={t('dashboard.calendar.nextMonth')}
            >
              <ChevronRight size={14} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7">
            {dayHeaders.map((d, i) => (
              <div key={i} className="text-[10px] text-center text-gray-400 font-medium py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-7">
            {calendarCells.map((date, i) => {
              if (!date) return <div key={`empty-${i}`} className="h-7" />;
              const key = isoDayLocal(date);
              const dayEvents = eventsByDay.get(key) || [];
              const isToday = isSameDay(date, today);
              const isSelected = selectedDay && isSameDay(date, selectedDay);

              // Up to 3 unique dots by event_type (extra events beyond 3 collapse).
              const uniqueTypes = [...new Set(dayEvents.map(e => e.event_type))].slice(0, 3);
              const clickable = dayEvents.length > 0;

              return (
                <button
                  key={key}
                  onClick={() => clickable && setSelectedDay(isSelected ? null : date)}
                  disabled={!clickable}
                  className={[
                    'h-7 text-[11px] flex flex-col items-center justify-center relative transition-colors',
                    isSelected
                      ? 'bg-blue-50 text-blue-700 font-semibold rounded'
                      : isToday
                        ? 'text-blue-700 font-semibold'
                        : 'text-gray-700',
                    clickable ? 'hover:bg-gray-50 rounded cursor-pointer' : 'cursor-default',
                  ].join(' ')}
                >
                  <span className="leading-none">{date.getDate()}</span>
                  {uniqueTypes.length > 0 && (
                    <span className="flex gap-[2px] mt-[2px] leading-none">
                      {uniqueTypes.map((t, idx) => (
                        <span
                          key={idx}
                          className={`w-1 h-1 rounded-full ${(EVENT_TYPE_STYLES[t] || EVENT_TYPE_STYLES.scheduled).dot}`}
                        />
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Right: events list (selected day OR next 5 upcoming) ───── */}
        <div className="flex-1 min-w-0 mt-4 md:mt-0">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-9 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          ) : listEvents.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p className="text-xs">
                {selectedDay
                  ? t('dashboard.calendar.emptyOnDay', { date: formatDateShort(t, fmt, selectedDay) })
                  : t('dashboard.calendar.emptyUpcoming')}
              </p>
            </div>
          ) : (
            <>
              <div className="text-[11px] text-gray-500 mb-1.5 flex items-center justify-between">
                <span>
                  {selectedDay
                    ? t('dashboard.calendar.countOnDay', { count: listEvents.length, date: formatDateShort(t, fmt, selectedDay) })
                    : t('dashboard.calendar.upcoming')}
                </span>
                {selectedDay && (
                  <button
                    onClick={() => setSelectedDay(null)}
                    className="text-blue-600 hover:underline"
                  >
                    {t('dashboard.calendar.showAll')}
                  </button>
                )}
              </div>
              <div className="space-y-1">
                {listEvents.map((event) => {
                  const days = daysUntil(event.start_date);
                  const isUrgent = days !== null && days >= 0 && days <= 3;
                  const style = EVENT_TYPE_STYLES[event.event_type] || EVENT_TYPE_STYLES.scheduled;
                  const typeLabel = t(`dashboard.calendar.types.${EVENT_TYPE_STYLES[event.event_type] ? event.event_type : 'scheduled'}`);
                  return (
                    <button
                      key={event.id}
                      onClick={() => handleEventClick(event)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-gray-50 transition-colors ${isUrgent ? 'bg-red-50' : ''}`}
                    >
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${style.badge}`}>
                        {typeLabel}
                      </span>
                      <span className="text-xs text-gray-900 truncate flex-1">{event.title}</span>
                      <span className="flex items-center gap-1 shrink-0">
                        {isUrgent && <AlertTriangle size={11} className="text-red-500" />}
                        <span className={`text-[11px] ${isUrgent ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                          {formatDateShort(t, fmt, event.start_date)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

export default CalendarWidget;
